import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box, Typography, Card, CardContent, Grid, Stack, Button, Chip, Tooltip, Select, MenuItem, Table, TableHead, TableRow,
  TableCell, TableBody, Link, Alert, Skeleton, ButtonBase, IconButton,
} from '@mui/material';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import NewspaperOutlinedIcon from '@mui/icons-material/NewspaperOutlined';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import { dashboardApi, notificationsApi } from '../services/api';
import { useShell } from '../components/Layout';
import { can, canTab, isMember } from '../utils/access';
import PeriodPicker from '../components/common/PeriodPicker';
import NotificationsButton from '../components/common/NotificationsButton';
import MarketplaceIcon from '../components/common/MarketplaceIcon';
import Sparkline from '../components/common/Sparkline';
import DeltaBadge from '../components/common/DeltaBadge';
import InfoTooltip from '../components/common/InfoTooltip';
import SectionHeader from '../components/common/SectionHeader';
import { ErrorState, CardsSkeleton } from '../components/common/States';
import { periodFromSearch, periodQuery, periodSearch } from '../utils/period';
import { formatMoney as formatMoneyBase, formatNumber, formatPercent, formatDate, formatRange, formatTimeMsk, formatRelativeMsk, greeting, apiError, MISSING_LABELS, pluralize, formatDateTimeMsk,  } from '../utils/format';
import { tokens, marketplaceMeta } from '../theme';
import { MARKETPLACES, MP_SECTIONS, mpPath } from '../utils/marketplaces';

// Все суммы в аналитике — с копейками, как в отчётах WB.
const formatMoney = (v, opts) => formatMoneyBase(v, { kopecks: true, ...opts });


const SCOPE_PARAM = { wildberries: 'wb_account', ozon: 'ozon_account', yandex_market: 'yandex_account' };
const SCOPE_PARAMS = Object.values(SCOPE_PARAM);
const HINTS = {
  revenue: 'Выручка по цене продавца с учётом его скидок, за вычетом возвратов.\nИсточник: финансовые отчёты (детализация реализации) Wildberries.\nСкидки WB за счёт площадки выручку не уменьшают.\nЭто не «Оплатили покупатели» из калькулятора: там сумма после скидок WB.\nOzon: «Реализовано на сумму» из отчёта о реализации — только за полные месяцы, по которым Ozon выпустил отчёт.',
  profit: 'Чистая прибыль = К перечислению − реклама со «Счёта» продвижения − себестоимость − налоги.\nКомиссия WB уже удержана из суммы к перечислению и повторно не вычитается.',
  margin: 'Маржинальность = Чистая прибыль / Выручка × 100%.',
  toPay: 'К перечислению = К перечислению за товар + компенсации и коррекции (включая компенсацию скидки лояльности) − логистика − хранение − приёмка − штрафы − удержания − минимальный платёж и прочие услуги WB.\nOzon: «Итого к начислению» из отчёта о реализации + компенсации − логистика, возвраты, размещение, продвижение, эквайринг и прочие услуги из финансового отчёта.\nЭто сумма по отчётам площадок, а не окончательная прибыль бизнеса.',
};

const Metric = ({ label, hint, value, delta, deltaUnit, reason, footer, badge }) => (
  <Box sx={{ minWidth: 0 }}>
    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.75 }}>
      <Typography variant="body2" color="text.secondary" noWrap>{label}</Typography>
      {hint && <InfoTooltip title={hint} />}
    </Stack>
    <Typography sx={{ fontSize: { xs: '1.5rem', lg: '1.75rem' }, fontWeight: 700, letterSpacing: '-0.01em', lineHeight: 1.2, whiteSpace: 'nowrap' }}>
      {value}
    </Typography>
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
      {footer !== undefined ? (
        <Typography variant="body2" color="text.secondary">{footer}</Typography>
      ) : (
        <DeltaBadge value={delta} unit={deltaUnit} reason={reason} />
      )}
      {badge}
    </Stack>
  </Box>
);

const PreliminaryChip = ({ missing }) => (
  <Tooltip title={<>Прибыль предварительная:<br />{missing.map((m) => `• ${MISSING_LABELS[m] || m}`).join('\n')}</>}
    componentsProps={{ tooltip: { sx: { whiteSpace: 'pre-line' } } }}>
    <Chip size="small" label="предварительно" tabIndex={0}
      sx={{ height: 22, bgcolor: tokens.warningSoft, color: tokens.warning, fontWeight: 600, fontSize: '0.75rem' }} />
  </Tooltip>
);

const SyncStatus = ({ card, onOpenSync }) => {
  const s = card.sync || {};
  const map = {
    ready: { color: tokens.success, text: s.updated_at ? `обновлено ${formatTimeMsk(s.updated_at)}` : 'загружено' },
    loading: { color: tokens.primary, text: s.progress ? `загрузка ${s.progress.stages_done}/${s.progress.stages_total}` : 'загрузка' },
    partial: { color: tokens.warning, text: 'загружено частично' },
    stale: { color: tokens.warning, text: s.updated_at ? `устарело: ${formatRelativeMsk(s.updated_at)}` : 'данные устарели' },
    no_access: { color: tokens.error, text: 'нет доступа' },
    error: { color: tokens.error, text: 'ошибка загрузки' },
    no_data: { color: tokens.inkMuted, text: 'нет данных' },
    not_supported: { color: tokens.inkMuted, text: 'в разработке' },
  };
  const m = map[s.state] || map.no_data;
  const tip = s.problems?.length ? s.problems.join('\n') : s.state === 'ready'
    ? 'Все источники загружены. Это не означает, что остальные разделы API площадки работают без сбоев.'
    : '';
  const clickable = card.analytics_supported && card.accounts?.length && s.state !== 'ready';
  return (
    <Tooltip title={<Box sx={{ whiteSpace: 'pre-line' }}>{tip}</Box>} disableHoverListener={!tip}>
      <Box component="span" sx={{ display: 'inline-flex' }}>
      <ButtonBase
        onClick={(e) => { e.stopPropagation(); if (clickable) onOpenSync(); }}
        disabled={!clickable}
        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, borderRadius: 2, px: 0.75, py: 0.25, color: m.color, fontSize: '0.875rem', fontWeight: 500 }}
        aria-label={`Состояние данных: ${m.text}`}
      >
        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: m.color }} />
        {m.text}
      </ButtonBase>
      </Box>
    </Tooltip>
  );
};

const MarketplaceCard = ({ card, count, period, onScope, onOpen, onOpenSync, onAdd }) => {
  const meta = marketplaceMeta(card.marketplace);
  const wide = count === 1;
  const scopeValue = card.scope_account_id || 'all';
  const cmp = card.comparison || {};
  const header = (
    <Box sx={{ mb: 2.5 }}>
      <Stack direction="row" alignItems="center" spacing={1.75}>
        <MarketplaceIcon marketplace={card.marketplace} size={48} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="h3" component="h3" noWrap>{meta.label}</Typography>
          {card.accounts.length > 1 ? (
            <Select
              size="small"
              variant="standard"
              disableUnderline
              value={scopeValue}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => onScope(card.marketplace, e.target.value === 'all' ? null : e.target.value)}
              inputProps={{ 'aria-label': `Кабинет ${meta.label}` }}
              sx={{ fontSize: '0.875rem', color: tokens.inkSoft, maxWidth: '100%', '& .MuiSelect-select': { py: 0 } }}
            >
              <MenuItem value="all">Все кабинеты ({card.accounts.length})</MenuItem>
              {card.accounts.map((a) => <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>)}
            </Select>
          ) : (
            <Typography variant="body2" color="text.secondary" noWrap>{card.accounts[0]?.name}</Typography>
          )}
        </Box>
        {onAdd && (
          <Tooltip title={`Добавить ещё один кабинет ${meta.label}`}>
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); onAdd(); }} aria-label={`Добавить кабинет ${meta.label}`}
              sx={{ alignSelf: 'flex-start', border: `1px solid ${tokens.borderStrong}`, bgcolor: tokens.paper, '&:hover': { bgcolor: tokens.primarySoft, borderColor: tokens.primary } }}>
              <AddRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
      <Box sx={{ mt: 1, ml: -0.75 }}><SyncStatus card={card} onOpenSync={onOpenSync} /></Box>
    </Box>
  );

  if (!card.analytics_supported) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardContent>
          {header}
          <Typography variant="body2" color="text.secondary">
            Кабинет подключён, доступ проверен. Загрузка данных {meta.label} ещё не реализована, поэтому выручка, заказы и прибыль здесь не показываются.
            Площадка появится в сводке после запуска интеграции.
          </Typography>
          <Button component={RouterLink} to={mpPath(card.marketplace, 'connections')} size="small" sx={{ mt: 2, px: 0 }} endIcon={<ArrowForwardRoundedIcon />}>
            Управление подключением
          </Button>
        </CardContent>
      </Card>
    );
  }

  const loading = card.sync?.state === 'loading' && !card.has_data;
  return (
    <Card
      sx={{ height: '100%', cursor: 'pointer', transition: 'box-shadow .2s, border-color .2s', '&:hover': { boxShadow: tokens.shadowRaised, borderColor: tokens.borderStrong } }}
      onClick={onOpen}
      onKeyDown={(e) => { if (e.key === 'Enter') onOpen(); }}
      tabIndex={0}
      role="link"
      aria-label={`${meta.label}: подробная аналитика`}
    >
      <CardContent>
        {header}
        {loading ? (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Идёт первоначальная загрузка данных с начала 2026 года. Показатели появятся по мере загрузки.
            </Typography>
            <Button size="small" variant="outlined" onClick={(e) => { e.stopPropagation(); onOpenSync(); }}>Ход загрузки</Button>
          </Box>
        ) : (
          <>
            <Box sx={{ display: 'grid', gridTemplateColumns: wide ? { xs: '1fr 1fr', sm: 'repeat(3, minmax(0, 1fr))' } : 'minmax(0, 1.35fr) minmax(0, 0.8fr) minmax(0, 1.35fr)', gap: 1.5 }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" color="text.secondary">Выручка</Typography>
                <Typography sx={{ fontWeight: 700, fontSize: wide ? '1.35rem' : { xs: '1.1rem', xl: '1.2rem' }, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={formatMoney(card.revenue)}>{formatMoney(card.revenue)}</Typography>
                <DeltaBadge compact value={cmp.revenue} reason={cmp.reason} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" color="text.secondary">Заказы</Typography>
                <Typography sx={{ fontWeight: 700, fontSize: wide ? '1.35rem' : { xs: '1.1rem', xl: '1.2rem' }, whiteSpace: 'nowrap' }}>{card.orders_available ? formatNumber(card.orders) : '—'}</Typography>
                <DeltaBadge compact value={cmp.orders} reason={cmp.reason} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Stack direction="row" alignItems="center" spacing={0.25}>
                  <Typography variant="body2" color="text.secondary">Прибыль</Typography>
                  {!card.profit_final && (
                    <Tooltip title="Прибыль предварительная: см. сводку выше">
                      <Box component="span" tabIndex={0} sx={{ color: tokens.warning, fontWeight: 700, fontSize: '0.8rem' }}>*</Box>
                    </Tooltip>
                  )}
                </Stack>
                <Typography sx={{ fontWeight: 700, fontSize: wide ? '1.35rem' : { xs: '1.1rem', xl: '1.2rem' }, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={formatMoney(card.net_profit)}>{formatMoney(card.net_profit)}</Typography>
                <DeltaBadge compact value={cmp.net_profit} reason={cmp.reason} />
              </Box>
            </Box>
            <Box sx={{ mt: 2.5 }}>
              <Sparkline data={card.series} color={meta.main} height={wide ? 96 : 72}
                label={`${card.marketplace === 'ozon' ? 'Сумма заказов' : 'Выручка'} ${meta.label} по дням за ${period.days} ${pluralize(period.days, 'день', 'дня', 'дней')}`} />
              {card.series?.some((p) => p.source === 'operational') && (
                <Typography variant="caption" color="text.secondary">Пунктир — оперативные продажи, ещё не вошедшие в финансовый отчёт</Typography>
              )}
              {card.marketplace === 'ozon' && (
                <Typography variant="caption" color="text.secondary">График — сумма заказов по дням. Выручка и прибыль Ozon — за полные месяцы с отчётом о реализации</Typography>
              )}
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
};

// A marketplace that is not connected yet: the whole card is the "+" that connects it.
const ConnectCard = ({ marketplace, onConnect }) => {
  const meta = marketplaceMeta(marketplace);
  return (
    <ButtonBase onClick={onConnect} aria-label={`Подключить ${meta.label}`}
      sx={{ width: '100%', height: '100%', minHeight: 240, borderRadius: `${tokens.radius}px`, border: `2px dashed ${tokens.borderStrong}`, bgcolor: 'rgba(255,255,255,0.6)',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1.5, p: 3, textAlign: 'center', transition: 'all .2s',
        '&:hover': { borderColor: tokens.primary, bgcolor: tokens.paper, boxShadow: tokens.shadowRaised }, '&:hover .plus': { transform: 'scale(1.08)' } }}>
      <Box sx={{ position: 'relative' }}>
        <MarketplaceIcon marketplace={marketplace} size={64} />
        <Box className="plus" sx={{ position: 'absolute', right: -10, bottom: -10, width: 32, height: 32, borderRadius: '50%', bgcolor: tokens.primary, color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center', border: `3px solid ${tokens.paper}`, transition: 'transform .2s' }}>
          <AddRoundedIcon fontSize="small" />
        </Box>
      </Box>
      <Typography variant="h3" component="span">{meta.label}</Typography>
      <Typography variant="body2" color="text.secondary" component="span" sx={{ maxWidth: 260 }}>
        Подключите кабинет — данные загрузятся автоматически
      </Typography>
      <Box component="span" sx={{ mt: 0.5, px: 2, py: 0.75, borderRadius: 2.5, bgcolor: tokens.primary, color: '#fff', fontWeight: 600, fontSize: '0.9rem' }}>
        + Подключить
      </Box>
    </ButtonBase>
  );
};

const DailyTable = ({ daily, today }) => {
  const updated = daily.rows.map((r) => r.updated_at).filter(Boolean).sort()[0];
  const reasonText = { loading: 'Данные загружаются', not_supported: 'Интеграция в разработке' };
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ pb: 1 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={2} sx={{ mb: 1 }} flexWrap="wrap">
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="h3" component="h3">Ежедневные показатели</Typography>
            <Chip size="small" label="оперативно" sx={{ bgcolor: tokens.primarySoft, color: tokens.primaryDark }} />
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {formatDate(daily.date)}{updated ? ` · обновлено ${formatRelativeMsk(updated, today)}` : ''}
          </Typography>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Незавершённый текущий день по оперативным данным площадок; итоговые суммы уточнятся в финансовых отчётах. По Ozon оперативно известны только заказы.
        </Typography>
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" aria-label="Показатели за сегодня">
            <TableHead>
              <TableRow>
                <TableCell>Площадка</TableCell>
                <TableCell align="right">Заказы</TableCell>
                <TableCell align="right">Продажи</TableCell>
                <TableCell align="right">Выручка</TableCell>
                <TableCell align="right">Возвраты</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {daily.rows.map((r) => (
                <TableRow key={r.account_id}>
                  <TableCell sx={{ py: 1.5 }}>
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                      <MarketplaceIcon marketplace={r.marketplace} size={32} />
                      <Box sx={{ minWidth: 0, maxWidth: { xs: 150, sm: 220 } }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{marketplaceMeta(r.marketplace).label}</Typography>
                        <Typography variant="caption" color="text.secondary" noWrap component="div" title={r.account_name}>{r.account_name}</Typography>
                      </Box>
                    </Stack>
                  </TableCell>
                  {r.available ? (
                    <>
                      <TableCell align="right">
                        {formatNumber(r.orders)}
                        {r.cancelled > 0 && <Typography component="div" variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>отмен: {formatNumber(r.cancelled)}</Typography>}
                      </TableCell>
                      <TableCell align="right">{r.orders_only ? '—' : formatNumber(r.sales)}</TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{r.orders_only ? '—' : formatMoney(r.revenue)}</TableCell>
                      <TableCell align="right">{r.orders_only ? '—' : formatNumber(r.returns)}</TableCell>
                    </>
                  ) : (
                    <TableCell colSpan={4} align="right">
                      <Typography variant="body2" color="text.secondary">{reasonText[r.reason] || 'Нет данных'}</Typography>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </CardContent>
    </Card>
  );
};

// WB monitoring for clients: directions of the WB API MARQIT loads data from that are down now,
// and WB news that directly affect MARQIT data.
const NewsCard = ({ news }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Typography variant="h3" component="h3" sx={{ mb: 2 }}>Сбои и новости Wildberries</Typography>
      {news === null ? (
        <Skeleton variant="rounded" height={120} />
      ) : (
        <Stack spacing={1.5}>
          {(news.incidents || []).map((i) => (
            <Box key={i.key} sx={{ borderLeft: `4px solid ${tokens.error}`, bgcolor: tokens.errorSoft, borderRadius: 2, p: 1.5 }}>
              <Typography variant="subtitle2" sx={{ color: tokens.error }}>Сбой API Wildberries: {i.label}</Typography>
              <Typography variant="body2" color="text.secondary">
                Не обновляются {i.affects}{i.since ? ` — с ${formatDateTimeMsk(i.since)}` : ''}. MARQIT догрузит данные сам, когда WB восстановится.
              </Typography>
            </Box>
          ))}
          {(news.items || []).map((n) => (
            <Box key={n.id} sx={{ borderLeft: `4px solid ${n.severity === 'critical' ? tokens.warning : tokens.primary}`, bgcolor: tokens.primaryTint, borderRadius: 2, p: 1.5 }}>
              <Typography variant="caption" color="text.secondary">{n.published_at ? formatDate(n.published_at.slice(0, 10)) : ''}</Typography>
              <Typography variant="subtitle2">{n.title}</Typography>
              {n.impact && <Typography variant="body2" color="text.secondary">{n.impact}</Typography>}
              {n.url && <Link href={n.url} target="_blank" rel="noopener noreferrer" variant="body2">Новость в кабинете WB</Link>}
            </Box>
          ))}
          {!(news.incidents || []).length && !(news.items || []).length && (
            <Stack alignItems="flex-start" spacing={1.5} sx={{ py: 1 }}>
              <Box sx={{ width: 44, height: 44, borderRadius: 2.5, bgcolor: news.source_status === 'ok' ? tokens.successSoft : tokens.primarySoft, color: news.source_status === 'ok' ? tokens.success : tokens.primaryDark, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {news.source_status === 'ok' ? <CheckCircleRoundedIcon /> : <NewspaperOutlinedIcon />}
              </Box>
              <Typography variant="subtitle1">
                {news.source_status === 'ok' ? 'API Wildberries работает, важных изменений нет' : 'Мониторинг Wildberries ещё не запущен'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {news.source_status === 'ok'
                  ? `MARQIT проверяет доступность WB каждые 5 минут${news.checked_at ? ` (последняя проверка ${formatDateTimeMsk(news.checked_at)})` : ''} и покажет здесь сбои и новости WB, которые влияют на ваши данные.`
                  : 'Здесь появятся сбои API Wildberries и новости WB, которые влияют на ваши данные.'}
              </Typography>
            </Stack>
          )}
        </Stack>
      )}
    </CardContent>
  </Card>
);

const DashboardPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { profile, openConnect } = useShell();
  const member = isMember(profile);
  const period = useMemo(() => periodFromSearch(location.search), [location.search]);
  // chosen cabinet per marketplace, kept in the URL: { wb_account: id, ozon_account: id, ... }
  const scopeKey = SCOPE_PARAMS.map((k) => new URLSearchParams(location.search).get(k) || '').join('|');
  const scope = useMemo(() => {
    const ids = scopeKey.split('|');
    return Object.fromEntries(SCOPE_PARAMS.map((k, i) => [k, ids[i]]).filter(([, v]) => v));
  }, [scopeKey]);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [news, setNews] = useState(null);

  // only the latest request may fill the page: a slower answer for an older period is dropped
  const loadSeq = useRef(0);
  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    setLoading(true);
    setError('');
    try {
      const { data: d } = await dashboardApi.get({ ...periodQuery(period), ...scope });
      if (seq === loadSeq.current) setData(d);
    } catch (e) {
      if (seq === loadSeq.current) setError(apiError(e));
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [period, scope]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    notificationsApi.criticalNews().then((r) => setNews(r.data)).catch(() => setNews({ source_status: 'error', items: [] }));
  }, []);

  // keep the numbers fresh while the initial load is running
  useEffect(() => {
    if (!data?.marketplaces?.some((m) => m.sync?.state === 'loading')) return undefined;
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [data, load]);

  // after connecting from the dashboard: refresh the cards and show the new account's loading
  const onConnected = (result) => {
    load();
    if (result?.created) navigate(`/marketplaces/sync/${result.created.id}`);
  };

  const setPeriod = (p) => navigate({ search: periodSearch(p, scope) }, { replace: true });
  const setScope = (mp, accountId) => {
    const next = { ...scope };
    delete next[SCOPE_PARAM[mp]];
    if (accountId) next[SCOPE_PARAM[mp]] = accountId;
    navigate({ search: periodSearch(period, next) }, { replace: true });
  };

  const name = profile?.full_name ? profile.full_name.split(' ')[0] : '';
  const summary = data?.summary;
  const cards = data?.marketplaces || [];
  const supportedCards = cards.filter((c) => c.analytics_supported);
  const problems = cards.flatMap((c) => (c.sync?.problems || []).map((p, i) => ({
    mp: c.marketplace, text: p, account: c.sync?.problem_accounts?.[i] || c.accounts?.[0]?.id,
  })));

  return (
    <Box sx={{ maxWidth: 1480, mx: 'auto' }}>
      <Stack direction={{ xs: 'column', lg: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', lg: 'flex-start' }} spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="overline" sx={{ color: tokens.primaryDark }}>Сводка бизнеса</Typography>
          <Typography variant="h1" component="h1">{greeting()}{name ? `, ${name}` : ''}</Typography>
        </Box>
        <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ width: { xs: '100%', lg: 'auto' }, minWidth: 0 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}><PeriodPicker value={period} onChange={setPeriod} /></Box>
          <NotificationsButton />
        </Stack>
      </Stack>

      {error && <Box sx={{ mb: 3 }}><ErrorState message={error} onRetry={load} /></Box>}

      {loading && !data && (
        <Stack spacing={3}>
          <Skeleton variant="rounded" height={190} sx={{ borderRadius: 4 }} />
          <CardsSkeleton />
        </Stack>
      )}

      {data && data.accounts.length === 0 && member && (
        <Card sx={{ background: tokens.heroGradient, border: 'none', mb: 3 }}>
          <CardContent sx={{ p: { xs: 3, md: 5 } }}>
            <Typography variant="h2" sx={{ mb: 1 }}>Кабинеты вам ещё не открыты</Typography>
            <Typography color="text.secondary">Владелец аккаунта выбирает, какие кабинеты видят сотрудники, в разделе «Команда».</Typography>
          </CardContent>
        </Card>
      )}

      {data && data.accounts.length === 0 && !member && (
        <>
          <Card sx={{ background: tokens.heroGradient, border: 'none', mb: 3 }}>
            <CardContent sx={{ p: { xs: 3, md: 5 } }}>
              <Typography variant="h2" sx={{ mb: 1 }}>Подключите первый маркетплейс</Typography>
              <Typography color="text.secondary" sx={{ maxWidth: 640 }}>
                Нажмите «+» на нужной площадке и вставьте API-ключ. MARQIT проверит доступ и сам загрузит историю с начала 2026 года —
                без ручного создания и скачивания отчётов.
              </Typography>
            </CardContent>
          </Card>
          <Grid container spacing={2.5}>
            {MARKETPLACES.map((mp) => (
              <Grid key={mp} item xs={12} md={4}><ConnectCard marketplace={mp} onConnect={() => openConnect(mp, null, onConnected)} /></Grid>
            ))}
          </Grid>
        </>
      )}

      {data && data.accounts.length > 0 && (
        <Box sx={{ opacity: loading ? 0.6 : 1, transition: 'opacity .2s' }} aria-busy={loading}>
          {/* ── summary ── */}
          <Card sx={{ background: tokens.heroGradient, border: `1px solid ${tokens.border}`, mb: 4, position: 'relative', overflow: 'hidden' }}>
            <Box aria-hidden sx={{ position: 'absolute', right: -80, top: -120, width: 360, height: 360, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.35)' }} />
            <CardContent sx={{ p: { xs: 3, md: 4 }, position: 'relative' }}>
              <Grid container spacing={{ xs: 3, md: 0 }} alignItems="stretch">
                <Grid item xs={12} md={4.5} sx={{ pr: { md: 4 } }}>
                  <Typography sx={{ color: tokens.primaryDark, fontWeight: 600, mb: 1.5 }}>
                    {summary.included_marketplaces.length > 1 || summary.not_included_marketplaces.length === 0
                      ? 'Все подключённые маркетплейсы'
                      : summary.included_marketplaces.map((m) => marketplaceMeta(m).label).join(', ') || 'Нет площадок с аналитикой'}
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Typography sx={{ fontSize: { xs: '2.25rem', md: '2.75rem' }, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.1 }}>
                      {formatMoney(summary.revenue)}
                    </Typography>
                    <InfoTooltip title={HINTS.revenue} />
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5 }} flexWrap="wrap">
                    <Typography color="text.secondary">Выручка за период</Typography>
                    <DeltaBadge value={summary.comparison.revenue} reason={summary.comparison.reason} suffix={summary.comparison.revenue != null ? 'к прошлому' : null} />
                  </Stack>
                </Grid>
                {[
                  { label: 'Чистая прибыль', hint: HINTS.profit, value: formatMoney(summary.net_profit), delta: summary.comparison.net_profit,
                    badge: !summary.profit_final && summary.has_data ? <PreliminaryChip missing={summary.profit_missing} /> : null },
                  { label: 'Маржинальность', hint: HINTS.margin, value: summary.margin == null ? '—' : formatPercent(summary.margin), delta: summary.comparison.margin_pp, unit: 'pp' },
                  { label: 'К перечислению', hint: HINTS.toPay, value: formatMoney(summary.to_pay),
                    footer: `по ${summary.included_marketplaces.length} ${pluralize(summary.included_marketplaces.length, 'площадке', 'площадкам', 'площадкам')}` },
                ].map((m) => (
                  <Grid key={m.label} item xs={12} sm={4} md={2.5}
                    sx={{ borderLeft: { md: `1px solid ${tokens.borderStrong}` } }}>
                    {/* the item's own padding comes from the grid spacing; this keeps the text off the divider */}
                    <Box sx={{ pl: { md: 2.5, lg: 3 } }}>
                      <Metric label={m.label} hint={m.hint} value={m.value} delta={m.delta} deltaUnit={m.unit}
                        reason={summary.comparison.reason} footer={m.footer} badge={m.badge} />
                    </Box>
                  </Grid>
                ))}
              </Grid>
              {(summary.not_included_marketplaces.length > 0 || !summary.coverage.full || summary.other_currencies?.length > 0) && summary.has_data !== undefined && (
                <Stack spacing={0.5} sx={{ mt: 3, pt: 2, borderTop: `1px solid ${tokens.borderStrong}` }}>
                  {!summary.coverage.full && supportedCards.length > 0 && (
                    <Typography variant="body2" color="text.secondary">
                      {summary.coverage.finance_to
                        ? `Финансовые отчёты загружены по ${formatDate(summary.coverage.finance_to)}. `
                        : 'Финансовые отчёты за этот период ещё не загружены. '}
                      {summary.included_marketplaces.includes('ozon')
                        ? 'Ozon публикует отчёт о реализации раз в месяц (до 5-го числа следующего), поэтому его выручка и прибыль считаются только за полные месяцы. '
                        : ''}
                      {summary.operational && (summary.operational.sales > 0 || summary.operational.returns > 0)
                        ? `За ${formatRange(summary.coverage.uncovered_from, summary.coverage.uncovered_to)} есть оперативные продажи: ${formatNumber(summary.operational.sales)} шт. на ${formatMoney(summary.operational.revenue)} — они не входят в выручку и прибыль до выхода отчёта.`
                        : ''}
                    </Typography>
                  )}
                  {summary.not_included_marketplaces.length > 0 && (
                    <Typography variant="body2" color="text.secondary">
                      {summary.not_included_marketplaces.map((m) => marketplaceMeta(m).label).join(' и ')}: подключены, но пока не входят в сводку — загрузка данных этих площадок ещё не реализована.
                    </Typography>
                  )}
                  {summary.other_currencies?.map((c) => (
                    <Typography key={c.currency} variant="body2" color="text.secondary">
                      {formatNumber(c.rows)} операций в валюте {c.currency} не включены в суммы: конвертация валют не выполняется.
                    </Typography>
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>

          {/* ── marketplaces ── */}
          <SectionHeader
            title="Маркетплейсы"
            subtitle="Ключевые показатели по каждой площадке за выбранный период"
            action={supportedCards.length > 0 && (
              <Button component={RouterLink} to={`${mpPath(supportedCards[0].marketplace)}?${periodSearch(period, scope.wb_account ? { account: scope.wb_account } : {})}`}
                endIcon={<ArrowForwardRoundedIcon />} sx={{ fontWeight: 600 }}>
                Подробная аналитика
              </Button>
            )}
          />
          <Grid container spacing={2.5} sx={{ mb: 5 }}>
            {MARKETPLACES.map((mp) => {
              const c = cards.find((x) => x.marketplace === mp);
              return (
                <Grid key={mp} item xs={12} md={4}>
                  {c ? (
                    <MarketplaceCard
                      card={c}
                      count={3}
                      period={data.period}
                      onScope={setScope}
                      onAdd={member ? null : () => openConnect(mp, null, onConnected)}
                      onOpen={() => navigate(`${mpPath(c.marketplace)}?${periodSearch(period, c.scope_account_id ? { account: c.scope_account_id } : {})}`)}
                      onOpenSync={() => navigate(`/marketplaces/sync/${c.scope_account_id || c.sync?.problem_accounts?.[0] || c.accounts[0].id}`)}
                    />
                  ) : member ? null : (
                    <ConnectCard marketplace={mp} onConnect={() => openConnect(mp, null, onConnected)} />
                  )}
                </Grid>
              );
            })}
          </Grid>

          {/* ── today ── */}
          <SectionHeader title="Сегодня" subtitle="Оперативные показатели и важные изменения" />
          <Grid container spacing={2.5}>
            <Grid item xs={12} lg={7}>
              <DailyTable daily={data.daily} today={data.today} />
              {problems.length > 0 && (
                <Stack spacing={1.25} sx={{ mt: 2 }}>
                  {problems.slice(0, 3).map((p, i) => (
                    <ButtonBase key={i} component={RouterLink} to={`/marketplaces/sync/${p.account}`}
                      sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2, borderRadius: 3, bgcolor: tokens.errorSoft, textAlign: 'left', justifyContent: 'flex-start' }}>
                      <ErrorOutlineRoundedIcon sx={{ color: tokens.error }} />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" sx={{ color: tokens.error }}>{marketplaceMeta(p.mp).label}: {p.text}</Typography>
                        <Typography variant="body2" color="text.secondary">Откройте ход загрузки, чтобы увидеть причину и повторить этап.</Typography>
                      </Box>
                      <ChevronRightRoundedIcon sx={{ color: tokens.inkSoft }} />
                    </ButtonBase>
                  ))}
                </Stack>
              )}
            </Grid>
            <Grid item xs={12} lg={5}>
              <NewsCard news={news} />
            </Grid>
          </Grid>
          {summary.has_data === false && supportedCards.length > 0 && cards.every((c) => c.sync?.state !== 'loading') && (
            <Alert severity="info" sx={{ mt: 3 }}>
              За выбранный период нет операций. Выберите другой период или проверьте, что в кабинете были продажи.
            </Alert>
          )}
        </Box>
      )}
    </Box>
  );
};

// The summary needs the «Сводка и аналитика» section: an employee without it starts at the first
// marketplace tab its owner opened instead of an access error.
const DashboardRoute = () => {
  const { profile } = useShell();
  if (isMember(profile) && !can(profile, 'analytics')) {
    const tab = MP_SECTIONS.find((t) => canTab(profile, t.key));
    return <Navigate to={tab ? mpPath('wildberries', tab.key) : '/settings'} replace />;
  }
  return <DashboardPage />;
};

export default DashboardRoute;
